import { useCallback, useEffect, useState } from "react";
import axios from "axios";
import { Building2, Check, Link2, RefreshCw, ShieldCheck, Unlink, X } from "lucide-react";
import { toast } from "sonner";

const API = process.env.REACT_APP_BACKEND_URL;

const money = (pence, currency = "GBP") =>
  new Intl.NumberFormat("en-GB", { style: "currency", currency }).format((pence || 0) / 100);

export default function BankConnectionPanel() {
  const [status, setStatus] = useState(null);
  const [transactions, setTransactions] = useState([]);
  const [busy, setBusy] = useState(false);

  const load = useCallback(async () => {
    try {
      const statusResponse = await axios.get(API + "/api/open-banking/status", { withCredentials: true });
      setStatus(statusResponse.data);
      if (statusResponse.data.connected) {
        const transactionResponse = await axios.get(
          API + "/api/open-banking/transactions?limit=12",
          { withCredentials: true },
        );
        setTransactions(transactionResponse.data.items || []);
      } else {
        setTransactions([]);
      }
    } catch {
      toast.error("Couldn't load the bank connection.");
    }
  }, []);

  useEffect(() => {
    load();
    const result = new URLSearchParams(window.location.search).get("bank");
    if (result === "connected") toast.success("Bank connected securely.");
    if (result === "cancelled") toast.info("Bank connection cancelled.");
    if (result === "failed" || result === "invalid") toast.error("The bank connection could not be completed.");
  }, [load]);

  const connect = async () => {
    setBusy(true);
    try {
      const response = await axios.post(API + "/api/open-banking/connect", {}, { withCredentials: true });
      window.location.assign(response.data.authorization_url);
    } catch (error) {
      toast.error(error?.response?.data?.detail || "Couldn't start the bank connection.");
      setBusy(false);
    }
  };

  const sync = async () => {
    setBusy(true);
    try {
      const response = await axios.post(API + "/api/open-banking/sync", {}, { withCredentials: true });
      const count = response.data.imported;
      toast.success(count + " new transaction" + (count === 1 ? "" : "s") + " imported.");
      await load();
    } catch (error) {
      toast.error(error?.response?.data?.detail || "Couldn't refresh the bank feed.");
    } finally {
      setBusy(false);
    }
  };

  const approve = async (transaction) => {
    if (!transaction.suggested_expense_id) return;
    setBusy(true);
    try {
      await axios.post(
        API + "/api/open-banking/transactions/" + encodeURIComponent(transaction.id) + "/match",
        { expense_id: transaction.suggested_expense_id },
        { withCredentials: true },
      );
      toast.success("Transaction and receipt matched.");
      await load();
    } catch (error) {
      toast.error(error?.response?.data?.detail || "Couldn't approve the match.");
    } finally {
      setBusy(false);
    }
  };

  const ignore = async (transaction) => {
    setBusy(true);
    try {
      await axios.post(
        API + "/api/open-banking/transactions/" + encodeURIComponent(transaction.id) + "/ignore",
        {},
        { withCredentials: true },
      );
      await load();
    } catch (error) {
      toast.error(error?.response?.data?.detail || "Couldn't update the transaction.");
    } finally {
      setBusy(false);
    }
  };

  const setPurpose = async (connectionId, accountPurpose) => {
    setBusy(true);
    try {
      await axios.put(
        API + "/api/open-banking/connections/" + encodeURIComponent(connectionId) + "/purpose",
        { account_purpose: accountPurpose },
        { withCredentials: true },
      );
      toast.success("Account purpose saved.");
      await load();
    } catch {
      toast.error("Couldn't save the account purpose.");
    } finally {
      setBusy(false);
    }
  };

  const disconnect = async (connection) => {
    if (!window.confirm("Disconnect " + (connection.institution_name || "this bank") + "? Imported records will be retained.")) return;
    setBusy(true);
    try {
      await axios.delete(
        API + "/api/open-banking/connections/" + encodeURIComponent(connection.connection_id),
        { withCredentials: true },
      );
      toast.success("Bank disconnected.");
      await load();
    } catch {
      toast.error("Couldn't disconnect the bank.");
    } finally {
      setBusy(false);
    }
  };

  if (!status) {
    return <section className="rounded-2xl border border-slate-200 bg-white p-4 text-sm text-slate-500">Loading bank connection…</section>;
  }

  const vehicleCostPence = status.vehicle_cost_pence || 0;

  return (
    <section data-testid="bank-connection-panel" className="rounded-2xl border border-slate-200 bg-white p-4 dark:border-slate-700 dark:bg-slate-800">
      <div className="flex items-start gap-3">
        <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-xl bg-blue-50 text-blue-700">
          <Building2 className="h-5 w-5" aria-hidden="true" />
        </div>
        <div className="min-w-0 flex-1">
          <div className="flex flex-wrap items-center gap-2">
            <h2 className="text-sm font-extrabold text-slate-900 dark:text-white">Bank reconciliation</h2>
            <span className="rounded-full bg-blue-50 px-2 py-0.5 text-[10px] font-extrabold uppercase tracking-wide text-blue-700">
              TrueLayer · {status.environment}
            </span>
          </div>
          <p className="mt-1 text-xs leading-relaxed text-slate-500">
            Read-only transaction access. NDG cannot move money or see your bank password.
          </p>
        </div>
      </div>

      {!status.connected ? (
        <div className="mt-4 rounded-xl border border-slate-200 bg-slate-50 p-3">
          <div className="flex items-center gap-2 text-xs font-bold text-slate-700">
            <ShieldCheck className="h-4 w-4 text-emerald-600" aria-hidden="true" />
            {status.configured ? "Ready to connect through TrueLayer" : "TrueLayer account setup required"}
          </div>
          <p className="mt-1 text-[11px] leading-relaxed text-slate-500">
            {status.configured
              ? "You will choose your bank and approve account-information access in its own app."
              : "The NDG connection is built. Add the TrueLayer client details before connecting a real account."}
          </p>
          <button
            data-testid="connect-bank-button"
            type="button"
            disabled={busy || !status.configured}
            onClick={connect}
            className="mt-3 inline-flex min-h-11 w-full items-center justify-center gap-2 rounded-xl bg-blue-700 px-4 py-2.5 text-sm font-extrabold text-white disabled:cursor-not-allowed disabled:opacity-50"
          >
            <Link2 className="h-4 w-4" aria-hidden="true" />
            Connect a bank
          </button>
        </div>
      ) : (
        <>
          <div className="mt-4 space-y-2">
            {(status.connections || []).map((connection) => (
              <div key={connection.connection_id} className="rounded-xl border border-emerald-200 bg-emerald-50 p-3">
                <div className="flex items-start justify-between gap-3">
                  <div>
                    <p className="text-xs font-extrabold text-emerald-900">{connection.institution_name || "Connected bank"}</p>
                    <p className="mt-0.5 text-[11px] text-emerald-700">
                      {connection.last_synced_at ? "Last refreshed " + new Date(connection.last_synced_at).toLocaleString() : "Ready for first refresh"}
                    </p>
                  </div>
                  <button type="button" disabled={busy} onClick={() => disconnect(connection)} className="inline-flex min-h-9 items-center gap-1 text-xs font-bold text-red-700">
                    <Unlink className="h-3.5 w-3.5" /> Disconnect
                  </button>
                </div>
                <label className="mt-3 block">
                  <span className="text-[10px] font-extrabold uppercase tracking-wide text-slate-500">What this account is for</span>
                  <select
                    value={connection.account_purpose || "general_business"}
                    disabled={busy}
                    onChange={(event) => setPurpose(connection.connection_id, event.target.value)}
                    className="mt-1 min-h-11 w-full rounded-lg border border-slate-300 bg-white px-3 text-sm font-bold text-slate-800"
                  >
                    <option value="general_business">Business account</option>
                    <option value="vehicle_cost_log">Personal car cost log</option>
                    <option value="personal">Personal — exclude from accounts</option>
                  </select>
                </label>
              </div>
            ))}
          </div>

          <div className="mt-3 grid grid-cols-2 gap-2">
            <button type="button" disabled={busy} onClick={sync} className="inline-flex min-h-11 items-center justify-center gap-2 rounded-xl bg-emerald-700 px-3 py-2 text-xs font-extrabold text-white disabled:opacity-50">
              <RefreshCw className={"h-4 w-4 " + (busy ? "animate-spin" : "")} aria-hidden="true" />
              Refresh all
            </button>
            <button type="button" disabled={busy || !status.configured} onClick={connect} className="inline-flex min-h-11 items-center justify-center gap-2 rounded-xl bg-blue-700 px-3 py-2 text-xs font-extrabold text-white disabled:opacity-50">
              <Link2 className="h-4 w-4" />
              Add another bank
            </button>
          </div>

          <div className="mt-3 grid grid-cols-2 gap-2 text-center">
            <Count label="Matched" value={status.counts?.matched || 0} tone="emerald" />
            <Count label="Needs receipt" value={status.counts?.needs_receipt || 0} tone="amber" />
            <Count label="Car-cost entries" value={status.counts?.vehicle_cost || 0} tone="blue" />
            <Count label="Internal transfers" value={status.counts?.internal_transfer || 0} tone="slate" />
          </div>

          {vehicleCostPence > 0 && (
            <div className="mt-3 rounded-xl border border-blue-200 bg-blue-50 p-3">
              <p className="text-[10px] font-extrabold uppercase tracking-wide text-blue-700">Personal car spending · imported period</p>
              <p className="mt-1 text-2xl font-extrabold text-blue-950">{money(vehicleCostPence)}</p>
              <p className="mt-1 text-[11px] leading-relaxed text-blue-800">
                Visibility only. Kept outside deductible business expenses while mileage is used.
              </p>
            </div>
          )}

          <div className="mt-4 space-y-2">
            {transactions.length === 0 ? (
              <p className="rounded-xl bg-slate-50 p-3 text-xs text-slate-500">Refresh the bank feed to import transactions.</p>
            ) : transactions.map((transaction) => (
              <article key={transaction.id} className="rounded-xl border border-slate-200 p-3">
                <div className="flex items-start justify-between gap-3">
                  <div className="min-w-0">
                    <p className="truncate text-sm font-bold text-slate-900 dark:text-white">{transaction.merchant_name}</p>
                    <p className="text-[11px] text-slate-500">
                      {transaction.institution_name} · {transaction.date} · {labelFor(transaction.reconciliation_status)}
                    </p>
                  </div>
                  <p className={"shrink-0 text-sm font-extrabold " + (transaction.direction === "outgoing" ? "text-slate-900 dark:text-white" : "text-emerald-700")}>
                    {transaction.direction === "outgoing" ? "−" : "+"}{money(transaction.amount_pence, transaction.currency)}
                  </p>
                </div>
                {transaction.reconciliation_status === "suggested" && (
                  <div className="mt-2 flex gap-2">
                    <button type="button" disabled={busy} onClick={() => approve(transaction)} className="inline-flex min-h-9 flex-1 items-center justify-center gap-1 rounded-lg bg-emerald-700 px-3 text-xs font-extrabold text-white">
                      <Check className="h-3.5 w-3.5" /> Approve match
                    </button>
                    <button type="button" disabled={busy} onClick={() => ignore(transaction)} className="inline-flex min-h-9 items-center justify-center gap-1 rounded-lg border border-slate-300 px-3 text-xs font-bold text-slate-600">
                      <X className="h-3.5 w-3.5" /> Not business
                    </button>
                  </div>
                )}
                {transaction.reconciliation_status === "needs_receipt" && (
                  <button type="button" disabled={busy} onClick={() => ignore(transaction)} className="mt-2 inline-flex min-h-9 items-center gap-1 rounded-lg border border-slate-300 px-3 text-xs font-bold text-slate-600">
                    <X className="h-3.5 w-3.5" /> Not business
                  </button>
                )}
              </article>
            ))}
          </div>
        </>
      )}
    </section>
  );
}

function Count({ label, value, tone }) {
  const classes = {
    emerald: "bg-emerald-50 text-emerald-800",
    blue: "bg-blue-50 text-blue-800",
    amber: "bg-amber-50 text-amber-800",
    slate: "bg-slate-100 text-slate-700",
  };
  return (
    <div className={"rounded-xl p-2 " + classes[tone]}>
      <p className="text-lg font-extrabold">{value}</p>
      <p className="text-[10px] font-bold">{label}</p>
    </div>
  );
}

function labelFor(status) {
  return {
    matched: "Matched",
    suggested: "Suggested match",
    needs_receipt: "Needs receipt",
    vehicle_cost: "Car cost log",
    internal_transfer: "Internal transfer",
    ignored: "Not included",
  }[status] || "Needs checking";
}
